import {
  Box,
  Card,
  Grid,
  IconButton,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AppContext } from "context/appContext";
import { TOAST_TYPE, ToastContext } from "context/toastContext";
import React, {
  ReactNode,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Await, useFetcher, useLoaderData } from "react-router";
import {
  EntityIDMap,
  Operation,
  Permission,
  ROLES,
  ScopeIDMap,
  TOWPAY_ROLE_IDS,
  ScopeType,
} from "types";
import {
  AUTURA_ASPHALT_LIGHT,
  AUTURA_TYPOGRAPHY,
  calculateOperations,
  getInitialPermissionState,
  matchPermissions,
  mobileCardSx,
  tabContainerMobileSx,
  tabSx,
  vertTabContainerSx,
} from "utils";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import PermissionsPanelSkeleton from "components/skeletons/PermissionsPanel";
import ContactPermissions from "pages/permissions/ContactPermissions";
import JobPermissions from "pages/permissions/JobPermissions";
import PermissionsPermission from "pages/permissions/PermissionsPermission";
import PropertyGroupPermissions from "pages/permissions/PropertyGroupPermissions";
import PropertyPermissions from "pages/permissions/PropertyPermissions";
import RuleAndViolationPermissions from "pages/permissions/RuleAndViolationPermissions";
import UserPermissions from "pages/permissions/UserPermissions";
import { ROUTES } from "router";
import AuthService from "utils/AuthService";
import { PermissionsActionResponse } from "./permissionsAction";
import { PermissionsLoaderData } from "./permissionsLoader";
import PermitPermissions from "./PermitPermissions";
import MobileTabSelect from "components/layout/MobileTabSelect";

export interface InitialEntityPermissionState {
  initialRead: ScopeType;
  initialCreate: ScopeType;
  initialUpdate: ScopeType;
}
export interface EntityPermissionsProps {
  roleID: number;
  entityID: number;
  initialState: InitialEntityPermissionState;
  mobile?: boolean;
  handleSubmit: (
    roleID: number,
    entityID: number,
    event: React.ChangeEvent<HTMLInputElement>,
    handler: Operation,
  ) => void;
  readonly?: boolean;
}

const PANEL_ORDER: Array<number> = [
  EntityIDMap.User,
  EntityIDMap.Job,
  EntityIDMap.Property,
  EntityIDMap.Property_Group,
  EntityIDMap.Rule,
  EntityIDMap.Contact,
  EntityIDMap.Permission,
];

const PermissionsPage = (): ReactNode => {
  const { palette, breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down("lg"));
  const isPhone = useMediaQuery(breakpoints.down("sm"));
  const displayToast = useContext(ToastContext);
  const { state } = useContext(AppContext);
  const { _permissions } = useLoaderData() as PermissionsLoaderData;
  const [loaderResolved, setLoaderResolved] = useState(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const fetcher = useFetcher();
  const readonly =
    !AuthService.IsSuperAdmin() && !state.entityPermissions.permission.update;

  useEffect(() => {
    const awaitData = async () => {
      const permissions = await _permissions;
      setPermissions(permissions);
      setLoaderResolved(true);
    };
    awaitData();
  }, [loaderResolved]);

  const response = fetcher.data as PermissionsActionResponse;
  useEffect(() => {
    if (response && !response.success) {
      displayToast({ type: TOAST_TYPE.ERROR, message: response.message });
    }
    setLoaderResolved(false);
  }, [fetcher]);

  const [role, setRole] = useState<string>(ROLES[1].name);

  const roles = useMemo(() => {
    const canSeeTowpayRoles = AuthService.IsSuperAdmin() || AuthService.IsTheOne();
    return ROLES.filter(
      (role) =>
        role.id !== 1 &&
        (canSeeTowpayRoles || !TOWPAY_ROLE_IDS.includes(role.id)),
    );
  }, [ROLES]);

  const handleScrollLeft = () => {
    const r = roles.findIndex((r) => r.name === role);
    if (r > 0) setRole(roles[r - 1].name);
  };

  const handleScrollRight = () => {
    const r = roles.findIndex((r) => r.name === role);
    if (r < roles.length - 1) setRole(roles[r + 1].name);
  };

  const handleChange = (event: React.SyntheticEvent, newRole: string) => {
    setRole(newRole);
  };

  const getPermission = (
    roleID: number,
    entityID: number,
    scopeID: number,
  ): Permission | undefined => {
    const signature = {
      roleID,
      entityID,
      scopeID,
      tenantID: state.tenant.id,
    };
    return permissions.find((p) => matchPermissions(p, signature));
  };

  const handleSubmit = (
    roleID: number,
    entityID: number,
    event: React.ChangeEvent<HTMLInputElement>,
    handler: Operation,
  ) => {
    const ops = calculateOperations(
      getPermission(roleID, entityID, ScopeIDMap.Role),
      getPermission(roleID, entityID, ScopeIDMap.AssignedToUser),
      getPermission(roleID, entityID, ScopeIDMap.CreatedByUser),
      handler,
      event.target.value as ScopeType,
    );

    // submit for rule/ruleset/violation
    if (
      [EntityIDMap.Rule, EntityIDMap.Ruleset, EntityIDMap.Violation].includes(
        entityID,
      )
    ) {
      fetcher.submit(
        {
          data: {
            tenantID: state.tenant.id,
            permissionsArray: [
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Rule,
                },
                scope: {
                  id: ScopeIDMap.Role,
                },
                ...ops.roleScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Rule,
                },
                scope: {
                  id: ScopeIDMap.AssignedToUser,
                },
                ...ops.assignedScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Rule,
                },
                scope: {
                  id: ScopeIDMap.CreatedByUser,
                },
                ...ops.createdScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Ruleset,
                },
                scope: {
                  id: ScopeIDMap.Role,
                },
                ...ops.roleScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Ruleset,
                },
                scope: {
                  id: ScopeIDMap.AssignedToUser,
                },
                ...ops.assignedScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Ruleset,
                },
                scope: {
                  id: ScopeIDMap.CreatedByUser,
                },
                ...ops.createdScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Violation,
                },
                scope: {
                  id: ScopeIDMap.Role,
                },
                ...ops.roleScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Violation,
                },
                scope: {
                  id: ScopeIDMap.AssignedToUser,
                },
                ...ops.assignedScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Violation,
                },
                scope: {
                  id: ScopeIDMap.CreatedByUser,
                },
                ...ops.createdScope,
              },
            ],
          },
        },
        {
          method: "PUT",
          action: ROUTES.PERMISSIONS,
          encType: "application/json",
        },
      );
    } else if (
      [
        EntityIDMap.Permit,
        EntityIDMap.Permit_Type,
        EntityIDMap.Pricing,
      ].includes(entityID)
    ) {
      fetcher.submit(
        {
          data: {
            tenantID: state.tenant.id,
            permissionsArray: [
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Permit,
                },
                scope: {
                  id: ScopeIDMap.Role,
                },
                ...ops.roleScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Permit,
                },
                scope: {
                  id: ScopeIDMap.AssignedToUser,
                },
                ...ops.assignedScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Permit,
                },
                scope: {
                  id: ScopeIDMap.CreatedByUser,
                },
                ...ops.createdScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Permit_Type,
                },
                scope: {
                  id: ScopeIDMap.Role,
                },
                ...ops.roleScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Permit_Type,
                },
                scope: {
                  id: ScopeIDMap.AssignedToUser,
                },
                ...ops.assignedScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Permit_Type,
                },
                scope: {
                  id: ScopeIDMap.CreatedByUser,
                },
                ...ops.createdScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Pricing,
                },
                scope: {
                  id: ScopeIDMap.Role,
                },
                ...ops.roleScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Pricing,
                },
                scope: {
                  id: ScopeIDMap.AssignedToUser,
                },
                ...ops.assignedScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: EntityIDMap.Pricing,
                },
                scope: {
                  id: ScopeIDMap.CreatedByUser,
                },
                ...ops.createdScope,
              },
            ],
          },
        },
        {
          method: "PUT",
          action: ROUTES.PERMISSIONS,
          encType: "application/json",
        },
      );
    } else {
      fetcher.submit(
        {
          data: {
            tenantID: state.tenant.id,
            permissionsArray: [
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: entityID,
                },
                scope: {
                  id: ScopeIDMap.Role,
                },
                ...ops.roleScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: entityID,
                },
                scope: {
                  id: ScopeIDMap.AssignedToUser,
                },
                ...ops.assignedScope,
              },
              {
                role: {
                  id: roleID,
                },
                entity: {
                  id: entityID,
                },
                scope: {
                  id: ScopeIDMap.CreatedByUser,
                },
                ...ops.createdScope,
              },
            ],
          },
        },
        {
          method: "PUT",
          action: ROUTES.PERMISSIONS,
          encType: "application/json",
        },
      );
    }
  };

  return (
    <Card sx={isMobile ? mobileCardSx : { borderRadius: 0 }}>
      <TabContext value={role}>
        <Box sx={isMobile ? tabContainerMobileSx : vertTabContainerSx}>
          {isMobile ? (
            <MobileTabSelect
              value={role}
              setValue={(newRole) => {
                setRole(newRole.replace("_", " "));
              }}
              options={roles.map((role) => role.name)}
            />
          ) : (
            <TabList
              sx={{
                flex: 1,
                color: isMobile
                  ? palette.primary.contrastText
                  : palette.secondary.contrastText,
              }}
              onChange={handleChange}
              indicatorColor="primary"
              ScrollButtonComponent={({ direction }) => (
                <IconButton
                  disabled={!loaderResolved}
                  onClick={
                    direction === "left" ? handleScrollLeft : handleScrollRight
                  }
                >
                  {direction === "left" ? (
                    <ChevronLeftIcon
                      htmlColor={
                        role === roles[0].name
                          ? palette.primary.main
                          : palette.primary.contrastText
                      }
                      fontSize={isPhone ? "inherit" : "large"}
                    />
                  ) : (
                    <ChevronRightIcon
                      htmlColor={
                        role === roles[roles.length - 1].name
                          ? palette.primary.main
                          : palette.primary.contrastText
                      }
                      fontSize={isPhone ? "inherit" : "large"}
                    />
                  )}
                </IconButton>
              )}
              aria-label={`${role}`}
              textColor={"inherit"}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons={isMobile}
              allowScrollButtonsMobile={isMobile}
            >
              {roles.map((role) => (
                <Tab
                  key={role.id}
                  label={role.name}
                  value={role.name}
                  disabled={!loaderResolved}
                  sx={{
                    ...tabSx,
                    fontSize: AUTURA_TYPOGRAPHY.h6?.fontSize,
                    fontWeight: AUTURA_TYPOGRAPHY.h6?.fontWeight,
                    lineHeight: AUTURA_TYPOGRAPHY.h6?.lineHeight,
                    letterSpacing: AUTURA_TYPOGRAPHY.h6?.letterSpacing,
                  }}
                />
              ))}
            </TabList>
          )}
        </Box>
        <Suspense
          fallback={
            <Box sx={{ mt: { xs: "50px", lg: 0 } }}>
              <TabPanel
                key={"skeleton"}
                value={ROLES[1].name}
                sx={{
                  p: 0,
                  height: { xs: `calc(100dvh - 150px)`, lg: "77.5vh" },
                  overflowY: "scroll",
                }}
              >
                <Grid container width={"100%"}>
                  {PANEL_ORDER.map((ID) => (
                    <PermissionsPanelSkeleton key={ID} entityTypeID={ID} />
                  ))}
                </Grid>
              </TabPanel>
            </Box>
          }
        >
          <Await resolve={_permissions}>
            {(tenantPermissions) => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                {roles.map((role) => (
                  <TabPanel
                    key={role.id}
                    value={role.name}
                    sx={{
                      px: 0,
                      pt: 0,
                      width: "100%",
                      height: {
                        xs: `calc(100dvh - 150px)`,
                        lg: `calc(100dvh - 132px - 64px)`,
                      },
                      overflowY: "scroll",
                      backgroundColor: "white",
                    }}
                  >
                    <Grid container width={"100%"} spacing={0}>
                      <Grid
                        sx={{
                          border: "1px solid",
                          borderColor: AUTURA_ASPHALT_LIGHT,
                          p: 2,
                        }}
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <UserPermissions
                          readonly={readonly}
                          mobile={isMobile}
                          roleID={role.id}
                          entityID={EntityIDMap.User}
                          initialState={getInitialPermissionState(
                            tenantPermissions,
                            role.id,
                            EntityIDMap.User,
                            state.tenant.id,
                          )}
                          handleSubmit={handleSubmit}
                        />
                      </Grid>
                      <Grid
                        sx={{
                          border: "1px solid",
                          borderColor: AUTURA_ASPHALT_LIGHT,
                          p: 2,
                        }}
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <JobPermissions
                          readonly={readonly}
                          mobile={isMobile}
                          roleID={role.id}
                          entityID={EntityIDMap.Job}
                          initialState={getInitialPermissionState(
                            tenantPermissions,
                            role.id,
                            EntityIDMap.Job,
                            state.tenant.id,
                          )}
                          handleSubmit={handleSubmit}
                        />
                      </Grid>
                      <Grid
                        sx={{
                          border: "1px solid",
                          borderColor: AUTURA_ASPHALT_LIGHT,
                          p: 2,
                        }}
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <PropertyPermissions
                          readonly={readonly}
                          mobile={isMobile}
                          roleID={role.id}
                          entityID={EntityIDMap.Property}
                          initialState={getInitialPermissionState(
                            tenantPermissions,
                            role.id,
                            EntityIDMap.Property,
                            state.tenant.id,
                          )}
                          handleSubmit={handleSubmit}
                        />
                      </Grid>
                      <Grid
                        sx={{
                          border: "1px solid",
                          borderColor: AUTURA_ASPHALT_LIGHT,
                          p: 2,
                        }}
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <PropertyGroupPermissions
                          readonly={readonly}
                          mobile={isMobile}
                          roleID={role.id}
                          entityID={EntityIDMap.Property_Group}
                          initialState={getInitialPermissionState(
                            tenantPermissions,
                            role.id,
                            EntityIDMap.Property_Group,
                            state.tenant.id,
                          )}
                          handleSubmit={handleSubmit}
                        />
                      </Grid>
                      <Grid
                        sx={{
                          border: "1px solid",
                          borderColor: AUTURA_ASPHALT_LIGHT,
                          p: 2,
                        }}
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <RuleAndViolationPermissions
                          readonly={readonly}
                          mobile={isMobile}
                          roleID={role.id}
                          entityID={EntityIDMap.Rule}
                          initialState={getInitialPermissionState(
                            tenantPermissions,
                            role.id,
                            EntityIDMap.Rule,
                            state.tenant.id,
                          )}
                          handleSubmit={handleSubmit}
                        />
                      </Grid>
                      <Grid
                        sx={{
                          border: "1px solid",
                          borderColor: AUTURA_ASPHALT_LIGHT,
                          p: 2,
                        }}
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <ContactPermissions
                          readonly={readonly}
                          mobile={isMobile}
                          roleID={role.id}
                          entityID={EntityIDMap.Contact}
                          initialState={getInitialPermissionState(
                            tenantPermissions,
                            role.id,
                            EntityIDMap.Contact,
                            state.tenant.id,
                          )}
                          handleSubmit={handleSubmit}
                        />
                      </Grid>
                      {AuthService.IsSuperAdmin() && (
                        <Grid
                          sx={{
                            border: "1px solid",
                            borderColor: AUTURA_ASPHALT_LIGHT,
                            p: 2,
                          }}
                          size={{
                            xs: 12,
                            sm: 6,
                          }}
                        >
                          <PermissionsPermission
                            readonly={readonly}
                            mobile={isMobile}
                            roleID={role.id}
                            entityID={EntityIDMap.Permission}
                            initialState={getInitialPermissionState(
                              tenantPermissions,
                              role.id,
                              EntityIDMap.Permission,
                              state.tenant.id,
                            )}
                            handleSubmit={handleSubmit}
                          />
                        </Grid>
                      )}
                      <Grid
                        sx={{
                          border: "1px solid",
                          borderColor: AUTURA_ASPHALT_LIGHT,
                          p: 2,
                          position: "relative",
                        }}
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <PermitPermissions
                          readonly={readonly}
                          mobile={isMobile}
                          roleID={role.id}
                          entityID={EntityIDMap.Permit}
                          initialState={getInitialPermissionState(
                            tenantPermissions,
                            role.id,
                            EntityIDMap.Permit,
                            state.tenant.id,
                          )}
                          handleSubmit={handleSubmit}
                        />
                      </Grid>
                    </Grid>
                  </TabPanel>
                ))}
              </Box>
            )}
          </Await>
        </Suspense>
      </TabContext>
    </Card>
  );
};

export default PermissionsPage;
